<template>
    <div class="container">
        <!-- header with filters -->
        <div class="flex flex-col lg:justify-between lg:flex-row lg:items-center gap-6 mb-10 lg:gap-8 lg:mb-20">
            <div class="flex items-center gap-2 font-bold uppercase cursor-pointer lg:cursor-auto"
                 @click="filterOpen = !filterOpen">
                <span>{{ labels.filter }}</span>
            </div>
            <div class="flex items-center overflow-x-auto gap-4 lg:gap-5">
                <label class="flex items-center text-sm rounded-mid border cursor-pointer h-14 lg:h-20 px-4 lg:px-6"
                       :class="{'border-gray border-opacity-50': selected.length > 0, 'border-red bg-red text-white': selected.length === 0}"
                       @click="reset">
                    <span class="whitespace-nowrap">{{ labels.all }}</span>
                </label>
                <label v-for="category in filterCategories"
                       :key="category.id"
                       class="flex items-center text-sm rounded-mid border cursor-pointer h-14 lg:h-20 px-4 lg:px-6"
                       :class="{'border-gray border-opacity-50': !isSelected(category.id), 'border-red bg-red text-white': isSelected(category.id)}">
                    <span class="whitespace-nowrap">{{ category.label }}</span>
                    <input class="hidden" type="checkbox" :id="category.id" :value="category.id" v-model="selected">
                </label>
            </div>
        </div>

        <div class="flex justify-end mb-6">
            <span class="text-xs">{{ totalItems }} {{ labels.results }}</span>
        </div>

        <!-- search results -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-y-12 gap-x-6 mb-6">
            <a v-for="(item, index) in items"
               :key="index"
               class="relative flex flex-col border rounded-2xl p-5 pb-6 cursor-none lg:pb-4 group"
               :href="item.link"
               data-custom-cursor>
                <div class="relative h-0 overflow-hidden pt-full mb-8">
                    <img :src="item.imageUrls.lg || fallbackImage"
                         :alt="item.title"
                         class="absolute top-0 left-0 w-full h-full rounded-2xl object-cover transform transition-all ease-out duration-500 group-hover:scale-110"/>
                </div>
                <h3 v-if="item.title" class="text-lg line-clamp-2 h-12">{{ item.title }}</h3>
                <div class="border-b border-light-gray my-7"/>
                <p v-if="item.text" class="text-sm text-gray">{{ item.text }}</p>
                <custom-cursor></custom-cursor>
            </a>
        </div>
        <div v-show="loading" class="flex justify-center">
            <loading-spinner class="w-20 h-20 fill-current"/>
        </div>

        <!-- pagination -->
        <div v-if="hasMore" class="flex justify-end mt-12">
            <div class="flex items-center group" @click="loadMore">
                <button class="button group-hover:bg-dark-red">
                    {{ labels.more }} ({{ more }})
                </button>
                <button class="button flex items-center group-hover:bg-dark-red -ml-px !pt-3.5 !pb-3">
                    <icon name="chevron" class="w-4 h-4 fill-current lg:group-hover:animate-bounce"/>
                </button>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import Icon from '../base/Icon.vue';
import axios from 'axios';
import LoadingSpinner from '../base/LoadingSpinner.vue';
import { addToState, removeFromState } from '../../utils';
import CustomCursor from '../base/CustomCursor.vue';

export interface Category {
    id: string;
    label: string;
}

export interface SearchLabels {
    all: string;
    filter: string;
    headline: string;
    results: string;
    more: string;
}

export interface Result {
    imageUrls: Record<string, string>;
    title: string;
    text: string;
    link: string;
    categories: string[];
}

export interface ResultResponse {
    totalResults: number;
    teasers: Result[];
}

@Component({
    components: {
        LoadingSpinner,
        Icon,
        CustomCursor
    }
})
export default class SearchResults extends Vue {
    @Prop({ default: () => [] }) roots: string[];
    @Prop({ default: {} }) defaultCategories: Category[];
    @Prop({ default: {} }) filterCategories: Category[];
    @Prop({ default: {} }) labels: SearchLabels;
    @Prop({ default: 6 }) batchSize: number;
    @Prop({ required: true }) fallbackImage: string;

    apiBaseUrl = '/.rest/api/v2/teasers';

    selected: string[] = [];
    filterOpen = false;
    loading = false;

    totalItems = 0;
    offset = 0;
    items: Result[] = [];

    mounted(): void {
        if (!this.readHash()) {
            this.getResults(true);
        }
    }

    get apiUrl(): string {
        return `${this.$contextPath}${this.apiBaseUrl}?site=${this.$site}&locale=${this.$lang}&offset=${this.offset}` +
               `&limit=${this.batchSize}&${this.rootsString}&${this.selectedCategoriesString}`;
    }

    get rootsString(): string {
        return this.roots.map(s => 'root=' + s).join('&');
    }

    get selectedCategoriesString(): string {
        if (this.selected.length > 0) {
            return this.selected.map(s => 'category=' + s).join('&');
        }
        return this.defaultCategories.map(c => c.id).map(s => 'category=' + s).join('&');
    }

    get hasMore(): boolean {
        return this.totalItems > this.items.length;
    }

    get more(): number {
        return Math.min(this.batchSize, this.totalItems - this.items.length);
    }

    loadFromUrl(): void {
        const params = new URLSearchParams(window.location.search);
        if (params.has('categories')) {
            this.selected = params.get('categories').split(',');
        } else {
            this.getResults(true);
        }
    }

    reset(): void {
        this.selected = [];
    }

    isSelected(id: string): boolean {
        return this.selected.includes(id);
    }

    loadMore(): void {
        this.getResults(false);
    }

    readHash(): boolean {
        const hash = location.hash.replace(/^#/, '');
        const filter = hash.split('&').find(x => x.includes('categories:'));
        if (filter) {
            const split = filter.split(':');
            if (split.length === 2 && split[1].length > 0) {
                this.selected = split[1].split(',');
                return true;
            }
        }
        return false;
    }

    updateHash() {
        removeFromState('categories');
        this.selected.forEach(cat => {
            addToState('categories', cat);
        });
    }

    getResults(clear: boolean): void {
        this.loading = true;
        if (clear) {
            this.offset = 0;
            this.items = [];
        }
        axios.get(this.apiUrl)
            .then(res => {
                const result: ResultResponse = res.data;
                this.totalItems = result.totalResults;
                this.items.push(...result.teasers);
                this.offset = this.items.length;
            })
            .finally(() => {
                this.loading = false;
            });
    }

    @Watch('selected')
    watchSelected(): void {
        this.updateHash();
        this.getResults(true);
    }
}
</script>
